/**
 * Transforms a camelCase string to snake_case.
 */
export type CamelToSnakeCase<S extends string> =
  S extends `${infer T}${infer U}`
    ? `${T extends Capitalize<T>
        ? "_"
        : ""}${Lowercase<T>}${CamelToSnakeCase<U>}`
    : S;

/**
 * Converts the keys of an object from camelCase to snake_case.
 */
export type ToSnakeCase<T> = {
  [K in keyof T as CamelToSnakeCase<string & K>]: T[K];
};

/**
 * Converts the keys of an object to snake_case.
 *
 * @param props - The object whose keys need to be converted.
 * @returns The object with keys in snake_case format.
 */
export function convertKeysToSnakeCase<T>(props: T): ToSnakeCase<T> {
  return Object.keys(props as object).reduce((acc, key) => {
    const snakeCaseKey = key.replace(
      /[A-Z]/g,
      (match) => `_${match.toLowerCase()}`,
    ) as keyof ToSnakeCase<T>;
    acc[snakeCaseKey] = (props as any)[key];
    return acc;
  }, {} as ToSnakeCase<T>);
}

/**
 * Converts a snake_case string to camelCase.
 */
export type SnakeToCamelCase<S extends string> =
  S extends `${infer T}_${infer U}`
    ? `${Uncapitalize<T>}${Capitalize<SnakeToCamelCase<U>>}`
    : S;

/**
 * Converts the keys of an object from snake_case to camelCase.
 */
export type ToCamelCase<T> = {
  [K in keyof T as SnakeToCamelCase<string & K>]: T[K];
};

/**
 * Converts the keys of an object to camelCase.
 *
 * @param props - The object whose keys need to be converted.
 * @returns The object with keys in camelCase format.
 */
export function convertKeysToCamelCase<T>(props: T): ToCamelCase<T> {
  return Object.keys(props as object).reduce((acc, key) => {
    const camelCaseKey = key.replace(/(_\w)/g, (match) =>
      match[1].toUpperCase(),
    ) as keyof ToCamelCase<T>;
    acc[camelCaseKey] = (props as any)[key];
    return acc;
  }, {} as ToCamelCase<T>);
}

/**
 * Higher-order function that applies ShortCode functionality to a component.
 *
 * @param Component - The component to be wrapped with ShortCode functionality.
 * @param additionalProps - Added Properties
 * @param camelCaseKeys - Should we camelcase all properties
 * @returns A new component with ShortCode functionality applied.
 */
export function withShortCode<P extends object, AP extends object>(
  Component: React.ComponentType<P>,
  additionalProps?: AP,
  camelCaseKeys = true,
) {
  const ShortCodeComponent = (props: P & AP) => {
    const newProps = { ...props, ...additionalProps };

    const componentProps = camelCaseKeys
      ? convertKeysToCamelCase(newProps)
      : newProps;
    return <Component {...(componentProps as P)} />;
  };

  Object.assign(ShortCodeComponent, Component);

  return ShortCodeComponent;
}
